import { useState } from 'react'
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardCopyIcon,
  EllipsisIcon,
  Trash2Icon
} from 'lucide-react'
import {
  addDays,
  dateFromString,
  dateString,
  isoWeek,
  localDate,
  startOfWeek
} from '@coneno/c-sheet-shared'
import { SegmentedControl } from '@/components/forms/segmented-control'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SidebarTrigger } from '@/components/ui/sidebar'

function weekLabel(from: string, to: string): string {
  const formatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year:
      dateFromString(from).getFullYear() === dateFromString(to).getFullYear()
        ? undefined
        : 'numeric'
  })
  const endFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
  return `${formatter.format(dateFromString(from))} – ${endFormatter.format(dateFromString(to))}`
}

type WeekHeaderProps = {
  weekStart: string
  weekEnd: string
  onWeekChange(date: string): void
  showEveryone: boolean
  onShowEveryoneChange(value: boolean): void
  previousWeekCount: number
  currentWeekCount: number
  copying: boolean
  clearing: boolean
  onCopyPreviousWeek(): void
  onClearWeek(): void
}

/** Week title with picker and arrows, the people filter and the week actions menu. */
export function WeekHeader({
  weekStart,
  weekEnd,
  onWeekChange,
  showEveryone,
  onShowEveryoneChange,
  previousWeekCount,
  currentWeekCount,
  copying,
  clearing,
  onCopyPreviousWeek,
  onClearWeek
}: WeekHeaderProps): React.JSX.Element {
  const [weekPickerOpen, setWeekPickerOpen] = useState(false)

  return (
    <header className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="flex min-w-0 items-start gap-3">
        <SidebarTrigger aria-label="Open navigation" className="mt-0.5 md:hidden" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
            Project sheet
          </p>
          <nav aria-label="Week navigation" className="flex flex-wrap items-center gap-1">
            <Popover open={weekPickerOpen} onOpenChange={setWeekPickerOpen}>
              <h1 className="-ml-1.5">
                <PopoverTrigger
                  render={
                    <button
                      type="button"
                      className="flex items-baseline gap-2.5 rounded-md px-1.5 py-0.5 text-left outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50"
                    />
                  }
                >
                  {/* Each label reserves the width of a wide example, so the arrows stay put. */}
                  <span className="grid text-2xl font-bold tracking-tight tabular-nums">
                    <span className="col-start-1 row-start-1">Week {isoWeek(weekStart)}</span>
                    <span className="invisible col-start-1 row-start-1" aria-hidden="true">
                      Week 00
                    </span>
                  </span>{' '}
                  <span className="grid text-[15px] text-muted-foreground tabular-nums">
                    <span className="col-start-1 row-start-1">{weekLabel(weekStart, weekEnd)}</span>
                    <span className="invisible col-start-1 row-start-1" aria-hidden="true">
                      {weekLabel('2026-05-28', '2026-05-28')}
                    </span>
                  </span>
                  <ChevronDownIcon className="size-4 self-center text-muted-foreground" />
                </PopoverTrigger>
              </h1>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  weekStartsOn={1}
                  showWeekNumber
                  selected={dateFromString(weekStart)}
                  defaultMonth={dateFromString(weekStart)}
                  onSelect={(date) => {
                    if (!date) return
                    onWeekChange(dateString(date))
                    setWeekPickerOpen(false)
                  }}
                  autoFocus
                />
              </PopoverContent>
            </Popover>
            <Button
              size="icon-sm"
              variant="outline"
              aria-label="Previous week"
              onClick={() => onWeekChange(addDays(weekStart, -7))}
            >
              <ChevronLeftIcon />
            </Button>
            <Button
              size="icon-sm"
              variant="outline"
              aria-label="Next week"
              onClick={() => onWeekChange(addDays(weekStart, 7))}
            >
              <ChevronRightIcon />
            </Button>
            {weekStart === startOfWeek(localDate()) ? (
              <span className="ml-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                This week
              </span>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => onWeekChange(localDate())}>
                Back to this week
              </Button>
            )}
          </nav>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl
          ariaLabel="People shown"
          size="xs"
          value={showEveryone}
          onChange={onShowEveryoneChange}
          options={[
            { value: false, label: 'With entries' },
            { value: true, label: 'Everyone' }
          ]}
        />
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<Button size="icon-sm" variant="outline" aria-label="Week actions" />}
          >
            <EllipsisIcon />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-56">
            <DropdownMenuGroup>
              <DropdownMenuItem
                disabled={!previousWeekCount || copying}
                onClick={onCopyPreviousWeek}
              >
                <ClipboardCopyIcon />
                Copy previous week
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                disabled={!currentWeekCount || clearing}
                onClick={onClearWeek}
              >
                <Trash2Icon />
                Clear this week
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
